import * as React from "react";
import { Copy, Globe, Smartphone, Tv } from "lucide-react";
import { toast } from "sonner";

import type { Theme } from "@/data/config";
import { usePortal } from "@/features/portal/portal-context";
import { BrandPreviewFrame } from "@/features/preview/brand-preview-frame";

/** Uppercase section heading in the portal's voice. */
function SectionHead({ label, title, blurb }: { label: string; title: string; blurb: string }) {
  return (
    <div className="mb-6 text-center">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[.14em] text-brand">{label}</div>
      <h2 className="text-xl font-extrabold tracking-[-.02em] text-foreground">{title}</h2>
      <p className="mx-auto mt-1.5 max-w-[440px] text-[13px] text-muted-foreground">{blurb}</p>
    </div>
  );
}

/** Mac-style browser chrome wrapper, used for the "embed anywhere" stage. */
function BrowserChrome({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border-strong bg-bg-2 shadow-portal">
      <div className="flex items-center gap-2 border-b border-border bg-panel-2 px-3.5 py-2.5">
        <span className="flex gap-1.5">
          <span className="size-3 rounded-full bg-[#ff5f57]" />
          <span className="size-3 rounded-full bg-[#febc2e]" />
          <span className="size-3 rounded-full bg-[#28c840]" />
        </span>
        <span className="ml-2 flex-1 truncate rounded-md bg-field px-3 py-1 text-center text-[11.5px] text-text-3">
          {url}
        </span>
      </div>
      {children}
    </div>
  );
}

export function DemoPage() {
  const { activeBrand, previewReloadKey } = usePortal();

  if (!activeBrand) return null;

  const brandId = activeBrand.id;
  // The live app renders the brand's published (live) theme. Use it to tint the
  // "coming soon" placeholder cards so the page feels on-brand.
  const liveTheme: Theme | undefined =
    activeBrand.themes.find((t) => t.id === activeBrand.activeThemeId) ?? activeBrand.themes[0];

  const embedSnippet = `<script src="https://pop.in/embed/${brandId}.js"></script>`;

  function copy(text: string, label: string) {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(label + " copied"),
      () => toast.error("Couldn’t copy to clipboard"),
    );
  }

  return (
    <div className="mx-auto max-w-[860px]">
      {/* Page header */}
      <div className="mb-10 text-center">
        <h1 className="text-2xl font-extrabold tracking-[-.02em]">Demo</h1>
        <p className="mx-auto mb-6 mt-1.5 max-w-[520px] text-sm text-muted-foreground">
          Show partners how {activeBrand.name} looks in the wild — the real pop.in app, themed
          end-to-end with your live brand.
        </p>
      </div>

      <div className="flex flex-col gap-16">
        {/* In their pocket — mobile */}
        <section>
          <SectionHead
            label="Mobile"
            title="In their pocket"
            blurb="The full pop.in app, live and themed end-to-end."
          />
          <div className="grid place-items-center">
            <BrandPreviewFrame brandId={brandId} reloadKey={previewReloadKey} />
          </div>
        </section>

        {/* Embed anywhere */}
        <section>
          <SectionHead
            label="Embed"
            title="Embed anywhere"
            blurb="Drop pop.in into any partner site with a single script tag. It keeps your theme."
          />
          <BrowserChrome url={`${activeBrand.name.toLowerCase().replace(/\s+/g, "")}.example.com`}>
            <div className="relative grid min-h-[360px] place-items-center bg-background p-6">
              {/* Placeholder host-site content behind the embedded widget. */}
              <div className="pointer-events-none absolute inset-0 flex flex-col gap-3 p-6 opacity-40">
                <div className="h-4 w-1/3 rounded bg-panel-2" />
                <div className="h-3 w-2/3 rounded bg-panel-2" />
                <div className="h-3 w-1/2 rounded bg-panel-2" />
              </div>
              {/* The embedded live app, at mobile size. */}
              <div className="relative overflow-hidden rounded-[20px] border border-border-strong shadow-portal">
                <BrandPreviewFrame
                  brandId={brandId}
                  reloadKey={previewReloadKey}
                  framed={false}
                  scale={0.66}
                />
              </div>
            </div>
          </BrowserChrome>

          {/* Copyable brand ID — the raw identifier partners need for API / embed integration */}
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-field px-3.5 py-2.5">
            <span className="shrink-0 text-[11px] font-bold uppercase tracking-[.06em] text-text-3">
              Brand ID
            </span>
            <code className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-[12px] text-muted-foreground">
              {brandId}
            </code>
            <button
              type="button"
              onClick={() => copy(brandId, "Brand ID")}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-panel-2 px-2.5 py-1.5 text-[12px] font-semibold text-foreground transition-colors hover:text-brand"
            >
              <Copy className="size-3.5" /> Copy
            </button>
          </div>

          {/* Copyable embed snippet */}
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-field px-3.5 py-2.5">
            <code className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-[12px] text-muted-foreground">
              {embedSnippet}
            </code>
            <button
              type="button"
              onClick={() => copy(embedSnippet, "Embed snippet")}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-panel-2 px-2.5 py-1.5 text-[12px] font-semibold text-foreground transition-colors hover:text-brand"
            >
              <Copy className="size-3.5" /> Copy
            </button>
          </div>
        </section>

        {/* On the big screen — placeholder cards */}
        <section>
          <SectionHead
            label="More surfaces"
            title="Beyond the phone"
            blurb="The same brand, everywhere your players are. (Coming soon — placeholders for now.)"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { icon: Tv, title: "On your TV", blurb: "Cast the lobby to the big screen." },
              { icon: Smartphone, title: "Play along", blurb: "Second-screen play while you watch." },
              { icon: Globe, title: "On the web", blurb: "A standalone branded web app." },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="rounded-2xl border border-dashed border-border-strong bg-field p-5"
                >
                  <div
                    className="mb-3 grid size-9 place-items-center rounded-[10px] text-white"
                    style={{ background: `linear-gradient(135deg, ${liveTheme?.primary ?? "#7c3aed"}, ${liveTheme?.accent ?? "#e0379a"})` }}
                  >
                    <Icon className="size-[18px]" />
                  </div>
                  <div className="text-[14px] font-bold text-foreground">{c.title}</div>
                  <div className="mt-1 text-[12px] text-text-3">{c.blurb}</div>
                  <div className="mt-3 text-[11px] font-bold uppercase tracking-[.06em] text-text-3">
                    Soon
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
